<template>
  <v-container>
    <h1>Отчет зарплаты тренеров (группы). {{ branchName }}</h1>
    <!-- 3.1 Блок списка тренеров -->
    <div class="mb-5">
      <router-link to="/reports">
        Назад к списку отчётов
      </router-link>
    </div>

    <div class="d-flex align-center flex-wrap mb-4">
      <v-btn
        color="primary"
        class="mr-3 mb-2 mb-sm-0"
        @click="salaryDictDialog = true"
      >
        Справочники зарплат
      </v-btn>
      <v-alert
        v-if="expiredSalaryDicts.length"
        type="error"
        dense
        text
        class="mb-0 flex-grow-1"
        style="min-width: 240px;"
      >
        Истекли справочники ({{ expiredSalaryDicts.length }}):
        {{ expiredSalaryDicts.map(d => d.title || `#${d.id}`).join(', ') }}
      </v-alert>
    </div>

    <my-loader :loader="loader"></my-loader>

    <v-card class="mb-4">
      <v-card-title>Ранее сформированные отчёты</v-card-title>
      <v-btn
        v-if="activeReportId"
        text
        small
        @click="clearActiveReport"
      >
        ← Назад к ранее созданным отчетам
      </v-btn>

      <v-card-text>
        <v-progress-circular
          v-if="loader"
          indeterminate
          color="primary"
          size="40"
          class="my-4"
        />
        <v-data-table
          v-if="!loader && !activeReportId"
          :headers="pastReportHeaders"
          :items="report_list"
          hide-default-footer
          :items-per-page="-1"
        >
          <template v-slot:item.created="{ item }">
            <a
              href="#"
              @click.prevent="loadPastReport(item.id)"
            >
              {{ formatDateTime(item.created_at) }}
            </a>
          </template>

          <template v-slot:item.period="{ item }">
            {{ formatDate(item.date_from) }} — {{ formatDate(item.date_to) }}
          </template>

          <template v-slot:item.trainer_ids="{ item }">
            <div>
              <span
                v-for="id in sortedTrainerIds(item.trainer_ids)"
                :key="id"
                class="mr-1"
              >
                {{ getTrainerName(id) }}
              </span>
            </div>
          </template>

          <template v-slot:item.report_status="{ item }">
            <span class="d-flex align-center">
              {{ statusLabel(item.report_status) }}
              <v-progress-circular
                v-if="item.report_status === 'PROCESSING'"
                indeterminate
                color="primary"
                size="16"
                width="2"
                class="ml-2"
              />
            </span>
          </template>
        </v-data-table>

        <v-pagination
          v-if="!loader && !activeReportId && reportsTotalPages > 1"
          v-model="reportsPage"
          :length="reportsTotalPages"
          class="mt-4"
          @input="getStaffSalaryReports"
        />

        <!-- 3.2.2 Таблица с данными -->
        <template v-if="!loader && activeReportId">
          <div>
            Отчет {{ formatDate(reportData.date_from) }} – {{ formatDate(reportData.date_to) }}
            id: {{ activeReportId }}
          </div>
          <v-data-table
            v-if="!loader && activeReportId"
            :headers="headers"
            :items="reportData.report_json"
            class="mt-4"
            hide-default-footer
            :items-per-page="-1"
            :no-data-text="'Данные за выбранный период отсутствуют'"
          >
            <template v-slot:item.coach="{ item }">
              <a
                style="cursor: pointer; text-decoration: underline;"
                @click="showDetails(item)"
              >
                {{ item.title }}
              </a>
            </template>

            <template v-slot:item.action="{ item }">
              <v-btn
                small
                color="success"
                @click="exportCoachReportToExcel(item)"
              >
                Скачать в Excel
              </v-btn>
              <v-btn
                v-if="item.contract"
                class="ml-5"
                small
                color="success"
                @click="makeSpecification(item)"
              >
                Список спецификацию
              </v-btn>
            </template>
          </v-data-table>
          <v-btn
            v-if="reportData.report_json && reportData.report_json.length"
            color="success"
            class="mt-4"
            @click="exportAllCoachesReportToExcel"
          >
            Скачать в excel весь отчет
          </v-btn>
        </template>
      </v-card-text>
    </v-card>

    <!-- 3.2 Блок периода отчета -->
    <v-card>
      <v-card-title>Запросить новый отчет</v-card-title>

      <v-expansion-panels
        v-model="trainerPanel"
        flat
        class="elevation-1"
      >
        <v-expansion-panel flat>
          <v-expansion-panel-header
            class="white--text"
            style="background-color: #1976D2;"
          >
            Список тренеров ({{ selectedCoaches.length }} из {{ coaches.length }} выбрано)
          </v-expansion-panel-header>

          <v-expansion-panel-content
            class="pa-4"
            style="background-color: #f5f5f5;"
          >
            <v-row class="mb-2">
              <v-col cols="12">
                <v-btn
                  small
                  color="primary"
                  text
                  @click="toggleAllCoaches"
                >
                  {{ selectedCoaches.length === coaches.length ? 'Очистить выбор' : 'Выбрать всех' }}
                </v-btn>
              </v-col>
            </v-row>

            <v-row>
              <v-col
                v-if="loader && !coaches.length"
                cols="12"
                class="text-center"
              >
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="40"
                />
              </v-col>

              <v-col
                v-for="coach in sortedCoaches"
                :key="coach.id"
                cols="12"
                sm="4"
                class="pa-1"
              >
                <v-checkbox
                  v-model="selectedCoaches"
                  :label="coach.title"
                  :value="coach.id"
                  dense
                  hide-details
                  class="checkbox-compact"
                />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>

      <v-card-text>
        <!-- 3.2.1 Форма дат -->
        <v-row>
          <v-col
            cols="12"
            sm="6"
          >
            <v-menu
              v-model="menuFrom"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dateFrom"
                  label="Дата начала"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="dateFrom"
                @input="menuFrom = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
          <v-col
            cols="12"
            sm="6"
          >
            <v-menu
              v-model="menuTo"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dateTo"
                  label="Дата окончания"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="dateTo"
                @input="menuTo = false"
              ></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>

        <v-btn
          color="primary"
          :disabled="!dateFrom || !dateTo || selectedCoaches.length === 0"
          @click="createStaffSalaryReport"
        >
          Сформировать отчет
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- 3.2.3 Модальное окно с детализацией -->
    <v-dialog
      v-model="detailsDialog"
      max-width="1200"
    >
      <v-card>
        <v-card-title>Дополнительная информация: {{ selectedCoach?.title }}</v-card-title>
        <v-card-text>
          <v-progress-circular
            v-if="loader"
            indeterminate
            color="primary"
            size="40"
            class="my-4"
          />
          <v-data-table
            v-if="!loader"
            :headers="detailHeaders"
            :items="selectedCoach?.services || []"
            hide-default-footer
            :items-per-page="-1"
          >
            <template v-slot:item.start="{ item }">
              <div>
                {{ formatDateTime(item.start) }}
                <div
                  :style="{
                    fontSize: '0.6rem',
                    marginTop: '4px',
                    color: matchStatusColor(item.salary_match_status),
                  }"
                >
                  Справочник: {{ item.salary_dict_title || 'Не указан' }} <br>
                  <span
                    v-if="item.salary_match_status"
                    :style="{ color: matchStatusColor(item.salary_match_status), fontSize: '0.6rem' }"
                  >
                    ({{ salaryMatchStatusLabel(item.salary_match_status, item.salary_dict_found_by) }}
                    <template v-if="item.salary_dict_found_by">
                      · {{ item.salary_dict_found_by }}
                    </template>)
                  </span>
                  <span
                    v-else-if="item.salary_dict_found_by"
                    style="color: #999; font-size: 0.6rem;"
                  >
                    ({{ item.salary_dict_found_by }})
                  </span>
                </div>
              </div>
            </template>

            <template v-slot:item.has_assistant="{ item }">
              {{ item.has_assistant ? 'Да' : 'Нет' }}
            </template>

            <template v-slot:item.is_trainer_assistant="{ item }">
              {{ item.is_trainer_assistant ? 'Ассистент' : 'Тренер' }}
            </template>

            <template v-slot:item.age_group="{ item }">
              {{ item.age_group === 'kid' ? 'Детская' : (item.age_group === 'adult' ? 'Взрослая' : item.age_group) }}
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            @click="detailsDialog = false"
          >
            Закрыть
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="salaryDictDialog"
      max-width="1200px"
    >
      <v-card>
        <v-card-title>Справочники зарплат тренеров</v-card-title>
        <v-card-text style="max-height: 70vh; overflow-y: auto;">
          <v-alert
            v-if="salaryDictProblemsCount > 0"
            type="warning"
            dense
            class="mb-4"
          >
            {{ salaryDictProblemsCount }} справочник(ов) с проблемами
            (истёк, истекает, конфликт или неверные даты).
            Проверьте колонку «Статус».
          </v-alert>
          <!-- Таблица справочников -->
          <v-data-table
            v-if="!loader"
            :items="salary_dict"
            :headers="salaryDictHeaders"
            item-key="id"
            class="mb-4"
            dense
            hide-default-footer
            :items-per-page="-1"
          >
            <template v-slot:item.sport_type="{ item }">
              <td>{{ sportTypeMap[item.sport_type] || item.sport_type }}</td>
            </template>
            <template v-slot:item.staff_type="{ item }">
              <td>
                {{ typeof item.staff_type === 'number'
                  ? staffTypeMap[item.staff_type]
                  : item.staff_type }}
              </td>
            </template>
            <template v-slot:item.start_date="{ item }">
              <td>{{ formatDate(item.start_date) }}</td>
            </template>
            <template v-slot:item.end_date="{ item }">
              <td>{{ formatDate(item.end_date) }}</td>
            </template>
            <template v-slot:item.health_status="{ item }">
              <v-chip
                x-small
                :color="healthStatusColor(item.health && item.health.status)"
                dark
              >
                {{ healthStatusLabel(item.health && item.health.status) }}
              </v-chip>
              <div
                v-if="item.health && item.health.issues && item.health.issues.length"
                style="font-size: 0.7rem; color: #666; margin-top: 2px; max-width: 220px;"
              >
                {{ item.health.issues[0].message }}
              </div>
            </template>
            <template v-slot:item.action="{ item }">
              <a @click="editSalaryDict(item)">Редактировать</a>
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="error"
            @click="salaryDictDialog = false"
          >
            Закрыть
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-if="selectedDict"
      v-model="salaryDictEditDialog"
      max-width="800px"
    >
      <v-card v-if="form.assistant && form.without_assistant">
        <v-card-title>Редактирование справочника {{ selectedDict.title }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="form.title"
            label="Название справочника"
            class="mb-4"
          />

          <v-select
            v-model="form.sport_type_value"
            :items="sportTypeOptions"
            item-text="text"
            item-value="value"
            label="Вид спорта"
            outlined
            dense
            class="mb-4"
          />

          <v-select
            v-model="form.staff_type_value"
            :items="staffTypeOptions"
            item-text="text"
            item-value="value"
            label="Тип персонала"
            outlined
            dense
            class="mb-4"
          />

          <v-row>
            <v-col
              cols="12"
              sm="6"
            >
              <v-menu
                v-model="formStartMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.start_date"
                    label="Дата начала действия"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="form.start_date"
                  @input="formStartMenu = false"
                />
              </v-menu>
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-menu
                v-model="formEndMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.end_date"
                    label="Дата окончания действия"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="form.end_date"
                  @input="formEndMenu = false"
                />
              </v-menu>
            </v-col>
          </v-row>

          <v-text-field
            v-model="form.training_ids"
            label="Training IDs (через запятую)"
            class="mb-4"
          />
          <v-alert
            type="info"
            class="mb-4"
          >
            {{ tariffWithoutLabel }}
          </v-alert>
          <v-data-table
            :headers="salaryEditHeaders"
            :items="salaryRows(form.without_assistant)"
            hide-default-footer
            :items-per-page="-1"
            dense
          >
            <template v-slot:item.value="{ item }">
              <v-text-field
                v-model.number="form.without_assistant[item.key]"
                type="number"
                dense
                hide-details
              />
            </template>
          </v-data-table>

          <v-divider class="my-4" />

          <v-alert
            type="info"
            class="mb-4"
          >
            {{ tariffWithLabel }}
          </v-alert>
          <v-data-table
            :headers="salaryEditHeaders"
            :items="salaryRows(form.assistant)"
            hide-default-footer
            :items-per-page="-1"
            dense
          >
            <template v-slot:item.value="{ item }">
              <v-text-field
                v-model.number="form.assistant[item.key]"
                type="number"
                dense
                hide-details
              />
            </template>
          </v-data-table>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="success"
            @click="submitSalaryDict"
          >
            Сохранить
          </v-btn>
          <v-btn
            color="primary"
            @click="copySalaryDict"
          >
            Дублировать
          </v-btn>
          <v-btn
            color="error"
            @click="deleteSalaryDict"
          >
            Удалить
          </v-btn>
          <v-btn @click="salaryDictEditDialog = false">
            Отмена
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="salarySpecDialog"
      max-width="800px"
    >
      <v-card>
        <v-card-title>Создание спецификации для {{ selectedCoach?.title }}</v-card-title>
        <v-card-text>
          <!-- Лоадер -->
          <v-progress-circular
            v-if="specLoader"
            indeterminate
            color="primary"
            size="40"
            class="my-4"
          />

          <!-- Выбор ИП -->
          <v-select
            v-if="!specLoader"
            v-model="selectedIp"
            :items="individualsList"
            item-text="name"
            item-value="id"
            label="Выберите ИП"
            outlined
            dense
            class="mb-4"
            @change="loadContractAndSpecs"
          />

          <!-- Номер договора -->
          <div v-if="selectedIp && !specLoader">
            <v-alert
              v-if="contractId"
              type="success"
              dense
            >
              Номер договора: <strong>{{ contract.contract_number }}</strong>
            </v-alert>
            <v-alert
              v-else
              type="error"
              dense
            >
              Отсутствует номер договора у сотрудника с выбранным ИП
            </v-alert>
          </div>

          <!-- Данные спецификации -->
          <div
            v-if="contractId"
            class="mt-3"
          >
            <v-card
              outlined
              class="pa-3"
            >
              <div><strong>Количество тренировок:</strong> {{ selectedCoach?.services.length }}</div>
              <div>
                <strong>Общая сумма:</strong>
                {{ grossFromNet(selectedCoach?.total_salary, contract) }}
              </div>
            </v-card>
          </div>

          <!-- Последние 3 спецификации -->
          <!-- Последние спецификации -->
          <div
            v-if="salarySpecs.length"
            class="mt-4"
          >
            <h4>Последние спецификации:</h4>
            <v-data-table
              :headers="salarySpecsHeaders"
              :items="salarySpecs.slice(0, 5)"
              dense
              hide-default-footer
            >
              <template v-slot:item.created_at="{ item }">
                {{ item.created_at }}
              </template>

              <template v-slot:item.amount_total="{ item }">
                {{ item.amount_total }} ₽
              </template>

              <template v-slot:item.file_url="{ item }">
                <v-btn
                  v-if="item.file_url"
                  color="success"
                  small
                  :href="item.file_url"
                  target="_blank"
                >
                  Скачать
                </v-btn>
              </template>

              <template v-slot:item.actions="{ item }">
                <v-btn
                  small
                  color="error"
                  @click="deleteSpec(item)"
                >
                  Удалить
                </v-btn>
              </template>
            </v-data-table>
          </div>

          <v-alert
            v-else-if="selectedIp && !specLoader"
            type="info"
            class="mt-4"
          >
            Спецификаций для выбранного ИП нет.
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="success"
            :disabled="!selectedIp || !contractId"
            @click="createSpec"
          >
            Создать спецификацию
          </v-btn>
          <v-btn
            color="error"
            @click="salarySpecDialog = false"
          >
            Закрыть
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Api from '@/api/api'
import MyLoader from '@/components/MyLoader.vue'
import { getSalaryCalcCoefficient, grossFromNet } from '@/utils/salaryCalc'
import * as XLSX from 'xlsx'
import * as XLSXStyle from 'xlsx-js-style'

const NO_SHOW_ATTENDANCE = -1
const EXCEL_MEDIUM_BORDER = { style: 'medium', color: { rgb: '000000' } }

export default {
  name: 'CoachesSalary',
  components: {
    MyLoader,
  },

  data() {
    return {
      coaches: [],
      salary_dict: [],
      selectedCoaches: [],
      dateFrom: null,
      dateTo: null,
      menuFrom: false,
      menuTo: false,
      reportData: [],
      report_list: [],
      headers: [
        { text: 'Тренер', value: 'coach' },
        { text: 'Сумма зарплаты', value: 'total_salary' },
        { text: 'Действие', value: 'action', sortable: false },
      ],
      detailsDialog: false,
      selectedCoach: null,
      detailHeaders: [
        { text: 'Дата тренировки', value: 'start' },
        { text: 'Тег', value: 'id_tag' },
        { text: 'С ассистентом', value: 'has_assistant' },
        { text: 'Взрослая/Детская', value: 'age_group' },
        { text: 'Статус тренера', value: 'is_trainer_assistant' },
        { text: 'Кол-во людей', value: 'people_count' },
        { text: 'Клиент не пришел', value: 'no_show_count' },
        { text: 'Зарплата', value: 'salary' },
      ],
      pastReportHeaders: [
        { text: 'Создан', value: 'created' },
        { text: 'Период', value: 'period' },
        { text: 'Тренеры', value: 'trainer_ids' },
        { text: 'Статус', value: 'report_status' },
      ],
      loader: false,
      trainerPanel: null,
      activeReportId: null,

      salaryDictDialog: false,
      salaryDictHeaders: [
        { text: 'ID', value: 'id' },
        { text: 'Название', value: 'title' },
        { text: 'Вид спорта', value: 'sport_type' },
        { text: 'Тип тренера', value: 'staff_type' },
        { text: 'Дата начала', value: 'start_date' },
        { text: 'Дата окончания', value: 'end_date' },
        { text: 'Статус', value: 'health_status', sortable: false },
        { text: 'Действие', value: 'action', sortable: false },
      ],
      salaryDictHealthSummary: null,
      reportHealth: null,
      localMatchSummary: null,
      staffTypeMap: { 1: 'тренеры', 2: 'ассистенты' },
      staffTypeReverseMap: { тренеры: 1, ассистенты: 2 },
      healthStatusLabels: {
        ok: 'Работает',
        expired: 'Истёк',
        expiring_soon: 'Истекает',
        conflict: 'Конфликт',
        invalid_dates: 'Неверные даты',
        unused: 'Не используется',
      },
      matchStatusLabels: {
        ok: 'Ок',
        expired_tag: 'Справочник по тегу истёк',
        fallback: 'По виду спорта',
        missing: 'Не найден',
        conflict: 'Конфликт',
      },
      form: {
        id: null,
        title: '',
        training_ids: '',
        assistant: {},
        without_assistant: {},
        staff_type: 1,
        staff_type_value: 1,
        sport_type_value: 1,
        start_date: null,
        end_date: null,
      },
      staffTypeOptions: [
        { text: 'Тренеры', value: 1 },
        { text: 'Ассистенты', value: 2 },
      ],
      sportTypeOptions: [
        { text: 'Бадминтон', value: 1 },
        { text: 'Настольный теннис', value: 2 },
        { text: 'Сквош', value: 3 },
      ],
      salaryDictEditDialog: false,
      salaryEditHeaders: [
        { text: 'Кол-во человек', value: 'key' },
        { text: 'Сумма', value: 'value' },
      ],
      reportPollingInterval: null,
      reportsPage: 1,
      reportsTotalPages: 1,
      reportsCount: 0,
      selectedDict: null,
      sportTypeMap: {
        1: 'бадминтон',
        2: 'настольный теннис',
        3: 'сквош',
      },
      sportTypeReverseMap: {
        бадминтон: 1,
        'настольный теннис': 2,
        сквош: 3,
      },
      salarySpecDialog: false,
      specLoader: false,
      individualsList: [],
      selectedIp: null,
      contract: null,
      contractId: null,
      salarySpecs: [],
      salarySpecsHeaders: [
        { text: '№', value: 'spec_number', width: '70px' },
        { text: 'Дата', value: 'created_at', width: '120px' },
        { text: 'Сумма', value: 'amount_total', width: '100px' },
        {
          text: 'Файл', value: 'file_url', sortable: false, width: '80px',
        },
        {
          text: 'Действие', value: 'actions', sortable: false, width: '80px',
        },
      ],
      formStartMenu: false,
      formEndMenu: false,
    }
  },
  computed: {
    branchName() {
      const branchId = Number(this.$route.params.branch_id)
      if (branchId === 230691) return 'Space Химки'
      if (branchId === 992575) return 'Space ВДНХ'
      return ''
    },
    tariffWithoutLabel() {
      const val = this.form && this.form.staff_type_value
      return Number(val) === 2 ? 'Тариф для взрослых групп' : 'Тарифы без ассистента'
    },
    tariffWithLabel() {
      const val = this.form && this.form.staff_type_value
      return Number(val) === 2 ? 'Тарифы для детских групп' : 'Тарифы с ассистентом'
    },
    sortedCoaches() {
      return [...this.coaches].sort((a, b) => a.title.localeCompare(b.title, 'ru'))
    },
    salaryDictProblemsCount() {
      return (this.salary_dict || []).filter(
        item => item.health && item.health.status && item.health.status !== 'ok',
      ).length
    },
    expiredSalaryDicts() {
      return (this.salary_dict || []).filter(
        item => item.health && item.health.status === 'expired',
      )
    },
  },

  async mounted() {
    await this.getSalaryDict()
    const reportId = this.$route.params.report_id
    if (reportId) {
      this.loadPastReport(reportId, false)
    }
  },

  watch: {
    '$route.params.report_id'(reportId) {
      if (reportId && String(reportId) !== String(this.activeReportId)) {
        this.loadPastReport(reportId, false)
      } else if (!reportId && this.activeReportId) {
        this.activeReportId = null
        this.reportData = []
        this.reportHealth = null
        this.localMatchSummary = null
      }
    },
  },


  methods: {
    grossFromNet,
    getSalaryCalcCoefficient,

    healthStatusLabel(status) {
      return this.healthStatusLabels[status] || status || 'Работает'
    },

    healthStatusColor(status) {
      if (status === 'conflict' || status === 'invalid_dates' || status === 'expired') {
        return 'error'
      }
      if (status === 'expiring_soon' || status === 'unused') {
        return 'warning'
      }
      return 'success'
    },

    salaryMatchStatusLabel(status, foundBy = null) {
      if (status === 'expired_tag') {
        return 'Справочник по тегу истёк'
      }
      if (status === 'ok' || status === 'fallback') {
        if (foundBy && String(foundBy).startsWith('id_tag=')) return 'По тегу'
        if (foundBy && String(foundBy).startsWith('sport_type=')) return 'По виду спорта'
        return 'Ок'
      }
      return this.matchStatusLabels[status] || status || ''
    },

    matchStatusColor(status) {
      if (status === 'conflict' || status === 'missing' || status === 'expired_tag') {
        return '#c62828'
      }
      return '#999'
    },

    updateLocalMatchSummary() {
      const services = (this.reportData.report_json || [])
        .flatMap(coach => coach.services || [])
      const summary = {
        services: services.length,
        ok: 0,
        fallback: 0,
        expired_tag: 0,
        missing: 0,
        conflict: 0,
        zero_rate: 0,
        unmatched_tags: 0,
        unused_tagged_dicts: 0,
      }
      const unmatched = new Set()
      services.forEach(service => {
        const status = service.salary_match_status || 'ok'
        if (Object.prototype.hasOwnProperty.call(summary, status)) {
          summary[status] += 1
        }
        if (status === 'expired_tag' && service.id_tag) {
          unmatched.add(String(service.id_tag).trim())
        }
        if (
          (status === 'ok' || status === 'fallback')
          && Number(service.people_count) > 0
          && Number(service.salary) === 0
        ) {
          summary.zero_rate += 1
        }
      })
      summary.unmatched_tags = unmatched.size
      this.localMatchSummary = summary
    },

    async loadReportHealth(reportId) {
      try {
        const params = new URLSearchParams([['id', reportId]])
        const data = await Api.getStaffSalaryReportHealth({ params })
        this.reportHealth = data

        const unusedIds = new Set(
          ((data && data.unused_tagged_dicts) || []).map(item => item.id),
        )
        if (unusedIds.size && this.salary_dict && this.salary_dict.length) {
          this.salary_dict = this.salary_dict.map(dict => {
            if (!unusedIds.has(dict.id)) return dict
            const current = (dict.health && dict.health.status) || 'ok'
            if (current !== 'ok') return dict
            return {
              ...dict,
              health: {
                status: 'unused',
                issues: [{
                  code: 'unused_in_report',
                  severity: 'warning',
                  message: 'Теги справочника не встречаются в этом отчёте',
                }],
              },
            }
          })
        }
      } catch (error) {
        console.warn('Не удалось загрузить health отчёта', error)
        this.reportHealth = null
      }
    },

    async getSalaryDict() {
      this.selectedDict = []
      this.salaryDictEditDialog = false

      this.loader = true
      try {
        await Api.getSalaryDict().then(data => {
          this.$set(this, 'loader', false)
          this.$set(this, 'salary_dict', data)
          this.salaryDictHealthSummary = {
            problems: (data || []).filter(
              item => item.health && item.health.status && item.health.status !== 'ok',
            ).length,
          }
        })
        await this.getStaffByBranchId()
      } catch (error) {
        console.log(error)
        this.loader = false
      }
    },

    async getStaffByBranchId() {
      this.loader = true
      const params = new URLSearchParams([
        ['branch_id', this.$route.params.branch_id],
        ['report_type', 'group'],
      ])
      try {
        await Api.getStaffByBranchId({ params }).then(data => {
          this.$set(this, 'loader', false)
          this.$set(this, 'coaches', [...data].sort((a, b) => a.title.localeCompare(b.title, 'ru')))
        })
        await this.getStaffSalaryReports()
      } catch (error) {
        console.log(error)
        this.loader = false
      }
    },

    async createStaffSalaryReport() {
      this.loader = true
      try {
        await Api.createStaffSalaryReport({
          branch_id: this.$route.params.branch_id,
          date_from: this.dateFrom,
          date_to: this.dateTo,
          trainer_ids: this.selectedCoaches,
        }).then(data => {
          this.$set(this, 'loader', false)
          this.reportsPage = 1
          this.getStaffSalaryReports()
          console.log(data)
        })
      } catch (error) {
        console.log(error)
        this.loader = false
      }
    },

    async getStaffSalaryReports() {
      this.loader = true
      const params = new URLSearchParams([
        ['branch_id', this.$route.params.branch_id],
        ['page', this.reportsPage],
      ])
      try {
        const data = await Api.getStaffSalaryReports({ params })
        this.report_list = data.results || []
        this.reportsCount = data.count || 0
        this.reportsTotalPages = Math.max(1, Math.ceil(this.reportsCount / 10))
        this.loader = false

        const hasProcessing = this.report_list.some(r => r.report_status === 'PROCESSING')
        if (hasProcessing && !this.reportPollingInterval) {
          this.reportPollingInterval = setInterval(() => {
            this.getStaffSalaryReports()
          }, 10000)
        } else if (!hasProcessing && this.reportPollingInterval) {
          clearInterval(this.reportPollingInterval)
          this.reportPollingInterval = null
        }
      } catch (error) {
        if (error.response?.status === 404) {
          this.report_list = []
          this.reportsCount = 0
          this.reportsTotalPages = 1
        } else {
          console.log(error)
        }
        this.loader = false
      }
    },

    showDetails(item) {
      this.selectedCoach = item
      this.detailsDialog = true
    },

    processCoachSalaryData() {
      this.reportData.report_json.forEach(coach => {
        let totalSalary = 0

        const coachInfo = this.coaches.find(c => c.id === coach.id)
        if (!coachInfo) {
          console.warn(`Тренер с id=${coach.id} не найден`)
          return
        }

        const sportType = coachInfo.sport_type
        coach.contract = coachInfo.contract

        // Функция проверки, действует ли справочник на данную дату
        const isDictActiveForDate = (dict, serviceDateStr) => {
          if (!serviceDateStr) return true // Если нет даты тренировки, не фильтруем по датам

          // Извлекаем дату из строки вида "2025-10-13 19:00:00" -> "2025-10-13"
          const serviceDate = serviceDateStr.split(' ')[0] || serviceDateStr.split('T')[0]
          if (!serviceDate) return true

          const dictStartDate = dict.start_date
          const dictEndDate = dict.end_date

          // Если у справочника нет дат - он всегда действует
          if (!dictStartDate && !dictEndDate) return true

          // Проверяем диапазон: start_date <= serviceDate <= end_date
          if (dictStartDate && serviceDate < dictStartDate) return false
          if (dictEndDate && serviceDate > dictEndDate) return false

          return true
        }

        const normalizeSportType = v => {
          if (typeof v === 'number') return v
          if (!v) return null
          const key = String(v).toLowerCase().trim()
          if (Object.prototype.hasOwnProperty.call(this.sportTypeReverseMap, key)) {
            return this.sportTypeReverseMap[key]
          }
          const n = Number(v)
          return Number.isFinite(n) ? n : null
        }

        const coachSportCode = normalizeSportType(sportType)

        coach.services.forEach(service => {
          const records = service.records || []
          const clientRecords = records.filter(r => !r.assistant)
          const noShowCount = clientRecords.filter(
            r => Number(r.attendance) === NO_SHOW_ATTENDANCE,
          ).length
          const peopleCount = clientRecords.filter(
            r => Number(r.attendance) !== NO_SHOW_ATTENDANCE,
          ).length
          const hasAssistant = records.some(r => r.assistant === true)

          service.people_count = peopleCount
          service.no_show_count = noShowCount
          service.has_assistant = hasAssistant

          const idTag = service.id_tag
          const serviceDate = service.start // Дата тренировки

          // 1) Определяем пул справочников: тренеры (1) или ассистенты (2)
          const desiredStaffType = service.is_trainer_assistant ? 2 : 1
          const normalizeStaffType = v => {
            if (typeof v === 'number') return v
            if (!v) return 1
            const t = String(v).toLowerCase()
            if (t.includes('ассист')) return 2
            if (t.includes('трен')) return 1
            const n = Number(v)
            return Number.isFinite(n) ? n : 1
          }

          const candidateDicts = (this.salary_dict || [])
            .filter(dict => normalizeStaffType(dict.staff_type) === desiredStaffType)
            .filter(dict => isDictActiveForDate(dict, serviceDate))

          const dictsSameStaff = (this.salary_dict || [])
            .filter(dict => normalizeStaffType(dict.staff_type) === desiredStaffType)

          const dictHasTag = (dict, tag) => {
            const ids = (dict.training_ids || '')
              .split(',')
              .map(s => String(s).trim())
              .filter(Boolean)
            return ids.includes(tag)
          }

          const dictMatchesSport = dict => {
            const dictSport = normalizeSportType(dict.sport_type)
            if (coachSportCode != null && dictSport != null) {
              return dictSport === coachSportCode
            }
            return dict.sport_type === sportType
          }

          const isDictExpiredForDate = (dict, serviceDateStr) => {
            if (!serviceDateStr || !dict.end_date) return false
            const serviceDateOnly = serviceDateStr.split(' ')[0] || serviceDateStr.split('T')[0]
            return Boolean(serviceDateOnly && serviceDateOnly > dict.end_date)
          }

          let salaryEntry = null
          let foundBy = null
          let matchStatus = null
          let expiredTagDicts = []

          // 2) Ищем по id_tag + sport_type с учетом дат
          if (idTag) {
            const idTagStr = String(idTag).trim()
            const tagMatches = candidateDicts.filter(dict => (
              dictHasTag(dict, idTagStr) && dictMatchesSport(dict)
            ))

            if (tagMatches.length > 1) {
              matchStatus = 'conflict'
              foundBy = `id_tag=${idTagStr}`
              service.salary = 0
              service.salary_dict_title = `Конфликт: ${tagMatches.map(d => d.title || `#${d.id}`).join(', ')}`
              service.salary_dict_found_by = foundBy
              service.salary_match_status = matchStatus
              return
            }

            if (tagMatches.length === 1) {
              salaryEntry = tagMatches[0]
              foundBy = `id_tag=${idTagStr}`
              matchStatus = 'ok'
            } else {
              // Активного справочника по тегу нет — ищем истёкшие с этим тегом
              expiredTagDicts = dictsSameStaff.filter(dict => (
                dictHasTag(dict, idTagStr)
                && dictMatchesSport(dict)
                && isDictExpiredForDate(dict, serviceDate)
              ))
            }
          }

          // 3) Fallback по виду спорта с учетом дат
          if (!salaryEntry) {
            const bySportType = candidateDicts.filter(dictMatchesSport)

            // Если искали по id_tag и не нашли, или изначально не было тега,
            // исключаем справочники с непустыми training_ids
            // (они предназначены для конкретных тегов, а не для общего fallback)
            const availableDicts = bySportType.filter(dict => !dict.training_ids || !dict.training_ids.trim())

            if (availableDicts.length > 1) {
              matchStatus = 'conflict'
              foundBy = `sport_type=${sportType}`
              service.salary = 0
              service.salary_dict_title = `Конфликт базовых: ${availableDicts.map(d => d.title || `#${d.id}`).join(', ')}`
              service.salary_dict_found_by = foundBy
              service.salary_match_status = matchStatus
              return
            }

            if (availableDicts.length === 1) {
              salaryEntry = availableDicts[0]
              foundBy = `sport_type=${sportType}`
              // Нет справочника по тегу — нормально. Важно только если спецсправочник
              // с этим тегом есть, но уже истёк.
              if (expiredTagDicts.length) {
                matchStatus = 'expired_tag'
                const expiredTitles = expiredTagDicts
                  .map(d => d.title || `#${d.id}`)
                  .join(', ')
                foundBy = `истёк: ${expiredTitles}`
              } else {
                matchStatus = 'ok'
              }
            }
          }

          coach.sport_type = sportType

          // 4) Если не нашли — выставляем 0 и предупреждаем
          if (!salaryEntry) {
            const serviceDateStr = serviceDate ? (serviceDate.split(' ')[0] || serviceDate.split('T')[0]) : 'неизвестна'
            if (expiredTagDicts.length) {
              const expiredTitles = expiredTagDicts
                .map(d => d.title || `#${d.id}`)
                .join(', ')
              console.warn(
                `Справочник по тегу истёк (staff_type=${desiredStaffType}) `
                + `id_tag=${idTag}, истёкшие: ${expiredTitles}, дата: ${serviceDateStr}`,
              )
              service.salary = 0
              service.salary_dict_title = `Истёк: ${expiredTitles}`
              service.salary_dict_found_by = `истёк: ${expiredTitles}`
              service.salary_match_status = 'expired_tag'
              return
            }
            console.warn(`Не найден справочник (staff_type=${desiredStaffType}) для sport_type=${sportType}, id_tag=${idTag}, дата тренировки: ${serviceDateStr}`)
            service.salary = 0
            service.salary_dict_title = 'Справочник не найден'
            service.salary_dict_found_by = null
            service.salary_match_status = 'missing'
            return
          }

          // Сохраняем информацию о справочнике сразу после выбора
          if (matchStatus === 'expired_tag' && expiredTagDicts.length) {
            const expiredTitles = expiredTagDicts
              .map(d => d.title || `#${d.id}`)
              .join(', ')
            service.salary_dict_title = `${salaryEntry.title || 'Базовый'} (вместо истёкшего: ${expiredTitles})`
          } else {
            service.salary_dict_title = salaryEntry.title || 'Не указан'
          }
          service.salary_dict_found_by = foundBy
          service.salary_match_status = matchStatus || 'ok'

          // 5) Выбор типа тарифа
          let salaryType
          if (desiredStaffType === 2) {
            // Пул ассистентов: маппинг по age_group
            const ag = (service.age_group || '').toLowerCase()
            if (ag === 'kid') {
              salaryType = 'assistant'
            } else if (ag === 'adult') {
              salaryType = 'without_assistant'
            } else {
              // дефолт (если age_group не пришёл)
              salaryType = 'without_assistant'
            }
          } else {
            // Пул тренеров: по наличию ассистента в записи
            salaryType = hasAssistant ? 'assistant' : 'without_assistant'
          }

          const salaryMap = salaryEntry[salaryType] || {}
          const salary = salaryMap[peopleCount] || salaryMap[String(peopleCount)] || 0

          service.salary = salary

          // Название справочника уже сохранено выше
          totalSalary += salary
        })

        coach.total_salary = totalSalary
      })

      this.reportData.report_json.sort((a, b) => a.title.localeCompare(b.title, 'ru'))
      this.updateLocalMatchSummary()
    },

    getAutoWidthColumns(data) {
      const colWidths = []

      if (!data.length) return colWidths

      const keys = Object.keys(data[0])

      keys.forEach((key, colIndex) => {
        const maxLength = Math.max(
          key.length,
          ...data.map(row => (row[key] ? String(row[key]).length : 0)),
        )
        colWidths[colIndex] = { wch: maxLength + 2 }
      })

      return colWidths
    },

    exportCoachReportToExcel(coach) {
      // Таблица 1: тренировки тренера
      console.log(coach)
      const summarySheet = coach.services.map(service => ({
        'Вид спорта': coach.sport_type,
        Тренировка: service.title,
        Дата: this.formatDateTime(service.start),
        Участников: service.people_count,
        'Клиент не пришел': service.no_show_count ?? 0,
        'Есть ассистент': service.has_assistant ? 'Да' : 'Нет',
        Справочник: service.salary_dict_title || '',
        'Статус подбора': this.salaryMatchStatusLabel(
          service.salary_match_status,
          service.salary_dict_found_by,
        ),
        'Оплата за тренировку': service.salary,
      }))

      summarySheet.push({})
      summarySheet.push({ Тренировка: 'ИТОГО', 'Оплата за тренировку': coach.total_salary })

      // Таблица 2: участники
      const participantsSheet = []

      coach.services.forEach(service => {
        (service.records || []).forEach(record => {
          participantsSheet.push({
            ФИО: record.name,
            'Тип клиента': record.client_type,
            Компания: record.company_name || '-',
            Тренировка: service.title,
            Дата: this.formatDateTime(service.start),
            Ассистент: record.assistant ? 'Да' : 'Нет',
            'Статус визита': record.attendance_label || (record.attendance != null ? record.attendance : ''),
          })
        })
      })

      // Создаем Excel-файл
      const wb = XLSX.utils.book_new()

      const ws1 = XLSX.utils.json_to_sheet(summarySheet)
      const ws2 = XLSX.utils.json_to_sheet(participantsSheet)

      XLSX.utils.book_append_sheet(wb, ws1, 'Тренировки')
      XLSX.utils.book_append_sheet(wb, ws2, 'Записи')

      // Устанавливаем ширину колонок
      ws1['!cols'] = this.getAutoWidthColumns(summarySheet)
      ws2['!cols'] = this.getAutoWidthColumns(participantsSheet)

      const filename = `Отчет_${coach.title.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`
      XLSX.writeFile(wb, filename)
    },

    exportAllCoachesReportToExcel() {
      const rows = []
      const coachBlocks = [] // { startRow, endRow } в 0-based индексах листа (0 = заголовок)

      ;(this.reportData.report_json || []).forEach(coach => {
        const lastName = (coach.title || '').trim().split(/\s+/)[0] || ''
        const blockStart = rows.length + 1 // +1 из‑за строки заголовка

        ;(coach.services || []).forEach(service => {
          rows.push({
            Фамилия: lastName,
            'Вид спорта': coach.sport_type,
            Тренировка: service.title,
            Дата: this.formatDateTime(service.start),
            Участников: service.people_count,
            'Клиент не пришел': service.no_show_count ?? 0,
            'Есть ассистент': service.has_assistant ? 'Да' : 'Нет',
            Справочник: service.salary_dict_title || '',
            'Статус подбора': this.salaryMatchStatusLabel(
              service.salary_match_status,
              service.salary_dict_found_by,
            ),
            'Оплата за тренировку': service.salary,
          })
        })

        rows.push({
          Фамилия: lastName,
          'Вид спорта': '',
          Тренировка: '',
          Дата: '',
          Участников: '',
          'Клиент не пришел': '',
          'Есть ассистент': '',
          Справочник: '',
          'Статус подбора': '',
          'Оплата за тренировку': '',
        })
        rows.push({
          Фамилия: lastName,
          'Вид спорта': '',
          Тренировка: 'ИТОГО',
          Дата: '',
          Участников: '',
          'Клиент не пришел': '',
          'Есть ассистент': '',
          Справочник: '',
          'Статус подбора': '',
          'Оплата за тренировку': coach.total_salary,
        })

        coachBlocks.push({ startRow: blockStart, endRow: rows.length })
      })

      const wb = XLSXStyle.utils.book_new()
      const ws = XLSXStyle.utils.json_to_sheet(rows)
      ws['!cols'] = this.getAutoWidthColumns(rows)

      coachBlocks.forEach(({ startRow, endRow }) => {
        this.applyExcelBlockBorders(ws, startRow, endRow, 8)
      })

      XLSXStyle.utils.book_append_sheet(wb, ws, 'Объединено')

      const filename = `Объединенный_отчет_${new Date().toISOString().slice(0, 10)}.xlsx`
      XLSXStyle.writeFile(wb, filename)
    },

    // Рамка medium вокруг блока тренера (как в образце объединённого отчёта)
    applyExcelBlockBorders(ws, startRow, endRow, colCount) {
      for (let r = startRow; r <= endRow; r += 1) {
        for (let c = 0; c < colCount; c += 1) {
          const addr = XLSXStyle.utils.encode_cell({ r, c })
          if (!ws[addr]) {
            ws[addr] = { t: 's', v: '' }
          }

          const border = {}
          if (r === startRow) border.top = EXCEL_MEDIUM_BORDER
          if (r === endRow) border.bottom = EXCEL_MEDIUM_BORDER
          if (c === 0) border.left = EXCEL_MEDIUM_BORDER
          if (c === colCount - 1) border.right = EXCEL_MEDIUM_BORDER

          if (Object.keys(border).length) {
            ws[addr].s = { ...(ws[addr].s || {}), border }
          }
        }
      }
    },

    async makeSpecification(coach) {
      console.log(coach)
      this.selectedCoach = coach
      this.salarySpecDialog = true
      this.specLoader = true
      this.individualsList = []
      this.salarySpecs = []
      this.selectedIp = null
      this.contractId = null
      this.contract = null

      try {
        const params = { params: { branch_id: this.$route.params.branch_id } }
        this.individualsList = await Api.getIndividualsList(params)
      } catch (e) {
        console.error(e)
        alert('Ошибка при загрузке списка ИП')
      } finally {
        this.specLoader = false
      }
    },

    async loadContractAndSpecs() {
      if (!this.selectedIp || !this.selectedCoach) return
      this.specLoader = true
      this.salarySpecs = []
      this.contractId = null
      this.contract = null

      try {
        // Получаем договор
        const contractParams = {
          params: {
            entrepreneur_id: this.selectedIp,
            staff_yc_id: this.selectedCoach.id,
          },
        }
        const contract = await Api.getСontractByIpAndAtaff(contractParams)
        this.contractId = contract?.id || null
        this.contract = contract || null

        // Загружаем последние спецификации только если договор есть
        if (this.contractId) {
          const specsParams = {
            params: {
              company_id: this.$route.params.branch_id,
              counterparty_name: contract.staff_name,
              entrepreneur_name: contract.entrepreneur_name,
            },
          }
          this.salarySpecs = await Api.getSalarySpecsList(specsParams)
        } else {
          this.salarySpecs = []
        }
      } catch (e) {
        console.error(e)
        this.contractId = null
        this.contract = null
        this.salarySpecs = []
      } finally {
        this.specLoader = false
      }
    },

    async createSpec() {
      if (!this.contractId || !this.selectedCoach || !this.selectedIp) {
        alert('Выберите ИП и дождитесь загрузки данных договора')
        return
      }

      try {
        const coefficient = getSalaryCalcCoefficient(this.contract)

        const payload = {
          contract_id: this.contractId,
          training_amount: this.selectedCoach.services.length,
          amount_due: Math.round((this.selectedCoach.total_salary) / this.selectedCoach.services.length / coefficient),
          amount_total: Math.round(this.selectedCoach.total_salary / coefficient),
          report_id: this.activeReportId,
        }

        await Api.createSalarSpec(payload)
        alert('Спецификация успешно создана')

        // Обновляем список спецификаций
        const specsParams = {
          params: {
            company_id: this.$route.params.branch_id,
            counterparty_name: this.contract.counterparty_name,
          },
        }
        this.salarySpecs = await Api.getSalarySpecsList(specsParams)

        // Автоматическое скачивание последнего файла
        if (this.salarySpecs.length > 0) {
          const lastSpec = this.salarySpecs[0]
          if (lastSpec.file_url) {
            window.open(lastSpec.file_url, '_blank')
          }
        }
      } catch (e) {
        console.error(e)
        let message = 'Ошибка при создании спецификации'

        // Проверка разных форматов ошибок
        if (e.response?.data) {
          const { data } = e.response
          if (typeof data === 'string') {
            message = data
          } else if (Array.isArray(data)) {
            message = data.join('\n')
          } else if (typeof data === 'object') {
            // Берём первое значение из объекта
            const firstKey = Object.keys(data)[0]
            if (Array.isArray(data[firstKey])) {
              message = data[firstKey].join('\n')
            } else {
              message = String(data[firstKey])
            }
          }
        }

        alert(message)
      }
    },

    getSportKey(sportType) {
      if (!sportType) return ''
      switch (sportType.toLowerCase()) {
        case 'бадминтон':
          return 'BADMINTON'
        case 'сквош':
          return 'SQUASH'
        case 'настольный теннис':
          return 'TABLE_TENNIS'
        default:
          return ''
      }
    },

    toggleAllCoaches() {
      if (this.selectedCoaches.length === this.coaches.length) {
        this.selectedCoaches = []
      } else {
        this.selectedCoaches = this.coaches.map(coach => coach.id)
      }
    },

    clearActiveReport() {
      this.activeReportId = null
      this.reportData = []
      this.reportHealth = null
      this.localMatchSummary = null
      this.$router.replace(`/reports/coaches_salary/${this.$route.params.branch_id}`)
    },

    loadPastReport(reportId, updateRoute = true) {
      this.activeReportId = reportId
      this.loader = true
      this.reportHealth = null
      this.localMatchSummary = null

      if (updateRoute && String(this.$route.params.report_id) !== String(reportId)) {
        this.$router.replace(
          `/reports/coaches_salary/${this.$route.params.branch_id}/${reportId}`,
        )
      }

      const params = new URLSearchParams([
        ['id', reportId],
      ])

      Api.getStaffSalaryReport({ params }).then(data => {
        this.$set(this, 'loader', false)
        this.$set(this, 'reportData', data)
        this.processCoachSalaryData()
        this.loadReportHealth(reportId)
      }).catch(error => {
        console.error(error)
        this.loader = false
      })
    },


    editSalaryDict(item) {
      this.selectedDict = item
      this.form.id = item.id
      this.form.title = item.title
      this.form.staff_type_value = this.staffTypeReverseMap[item.staff_type]
        ?? (typeof item.staff_type === 'number' ? item.staff_type : 1)
      this.form.sport_type_value = this.sportTypeReverseMap[item.sport_type]
        ?? (typeof item.sport_type === 'number' ? item.sport_type : 1)
      this.form.training_ids = item.training_ids
      this.form.assistant = { ...item.assistant }
      this.form.without_assistant = { ...item.without_assistant }
      this.form.start_date = item.start_date || null
      this.form.end_date = item.end_date || null
      this.salaryDictDialog = false
      this.salaryDictEditDialog = true
      console.log(item)
    },

    async submitSalaryDict() {
      try {
        const payload = {
          id: this.form.id,
          title: this.form.title,
          sport_type: this.form.sport_type_value,
          staff_type: this.form.staff_type_value,
          training_ids: this.form.training_ids,
          assistant: this.form.assistant,
          without_assistant: this.form.without_assistant,
          start_date: this.form.start_date,
          end_date: this.form.end_date,
        }

        await Api.updateSalaryDict(payload)

        this.salaryDictEditDialog = false
        this.salaryDictDialog = true
        this.form = { id: null, assistant_json: {}, without_assistant_json: {} }
        await this.getSalaryDict()
        alert('Данные справочника успешно обновлены')
      } catch (error) {
        const data = error && error.response && error.response.data
        if (data && data.conflicts && data.conflicts.length) {
          const messages = data.conflicts.map(c => c.message).join('\n')
          alert(`Конфликт справочников:\n${messages}`)
          return
        }
        alert(`Ошибка при сохранении: ${error}`)
      }
    },

    salaryRows(obj) {
      const max = 42
      const rows = []
      for (let i = 0; i <= max; i++) {
        rows.push({
          key: String(i),
          value: obj[i] || 0,
        })
      }
      return rows
    },

    getTrainerName(id) {
      const trainer = this.coaches.find(c => c.id === id)
      return trainer ? trainer.title : `#${id}`
    },

    sortedTrainerIds(ids) {
      return [...(ids || [])].sort((a, b) => this.getTrainerName(a).localeCompare(this.getTrainerName(b), 'ru'))
    },

    statusLabel(status) {
      switch (status) {
        case 'PROCESSING':
          return 'Формируется'
        case 'READY':
          return 'Готов'
        case 'ERROR':
          return 'Ошибка'
        default:
          return status
      }
    },
    formatDate(dateString) {
      if (!dateString) return ''

      const datePart = String(dateString).split(/[\sT]/)[0]
      const [year, month, day] = datePart.split('-').map(Number)

      // Создаем дату в локальном времени
      const date = new Date(year, month - 1, day)

      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    },

    formatDateTime(dateString) {
      if (!dateString) return ''

      const [datePart, timePart] = dateString.split(/[\sT]/)
      if (!datePart) return 'Неверная дата'

      const [year, month, day] = datePart.split('-').map(Number)
      let hours = 0
      let minutes = 0

      if (timePart) {
        [hours, minutes] = timePart.split(':').map(Number)
      }

      const date = new Date(year, month - 1, day, hours, minutes)

      if (isNaN(date)) return 'Неверная дата'

      return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    async copySalaryDict() {
      if (!this.selectedDict) return

      const confirmCopy = confirm(`Скопировать справочник "${this.selectedDict.title}"?`)
      if (!confirmCopy) return

      try {
        const normalizedStaffType = this.staffTypeReverseMap[this.selectedDict.staff_type]
          ?? (typeof this.selectedDict.staff_type === 'number' ? this.selectedDict.staff_type : 1)

        const payload = {
          title: `${this.selectedDict.title} (копия)`,
          sport_type: this.sportTypeReverseMap[this.selectedDict.sport_type] ?? this.selectedDict.sport_type,
          staff_type: normalizedStaffType,
          training_ids: this.selectedDict.training_ids,
          assistant: { ...this.selectedDict.assistant },
          without_assistant: { ...this.selectedDict.without_assistant },
          start_date: this.selectedDict.start_date || null,
          end_date: this.selectedDict.end_date || null,
        }

        await Api.createSalaryDict(payload)
        await this.getSalaryDict()
        alert('Справочник успешно скопирован')
      } catch (error) {
        console.error(error)
        const data = error && error.response && error.response.data
        if (data && data.conflicts && data.conflicts.length) {
          const messages = data.conflicts.map(c => c.message).join('\n')
          alert(`Конфликт справочников:\n${messages}`)
          return
        }
        alert('Ошибка при копировании справочника')
      }
    },

    async deleteSalaryDict() {
      if (!this.selectedDict) return

      // Подсчёт справочников с таким же sport_type
      const sameTypeCount = this.salary_dict.filter(
        dict => dict.sport_type === this.selectedDict.sport_type,
      ).length

      if (sameTypeCount <= 1) {
        alert(`Нельзя удалить единственный справочник по виду спорта: ${this.selectedDict.sport_type}`)
        return
      }

      const confirmDelete = confirm(`Удалить справочник "${this.selectedDict.title}"?`)
      if (!confirmDelete) return

      try {
        await Api.deleteSalaryDict({ data: { id: this.selectedDict.id } })
        await this.getSalaryDict()
        this.selectedDict = null
        alert('Справочник удалён')
      } catch (error) {
        console.error(error)
        alert('Ошибка при удалении справочника')
      }
    },
    async deleteSpec(spec) {
      const confirmDelete = confirm(`Удалить спецификацию №${spec.spec_number} от ${spec.created_at}?`)
      if (!confirmDelete) return

      try {
        const payload = {
          spec_id: spec.id,
        }
        await Api.deleteSalarSpec({
          params: { spec_id: spec.id },
        })
        alert('Спецификация удалена')

        // Обновляем список после удаления
        const specsParams = {
          params: {
            company_id: this.$route.params.branch_id,
            counterparty_name: this.contract.counterparty_name,
            entrepreneur_name: this.contract.entrepreneur_name,
          },
        }
        this.salarySpecs = await Api.getSalarySpecsList(specsParams)
      } catch (e) {
        console.error(e)
        alert('Ошибка при удалении спецификации')
      }
    },
  },
}
</script>

<style>
.v-expansion-panel-header__icon .v-icon {
  color: white !important;
}
</style>
